import { useState } from "react";
import { Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const inputCls = "w-full h-9 px-3 rounded-md bg-[#0B0F17] border border-[#242C3D] text-sm focus:border-indigo-500 focus:outline-none";
const str = (v) => (v == null ? "" : String(v));
const bad = (v, min = 0, max = 100) => v !== "" && (Number.isNaN(Number(v)) || Number(v) < min || Number(v) > max);

export const LeadOverrideEditor = ({ campaignId, lead, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [score, setScore] = useState("");
  const [pct, setPct] = useState("");
  const [sess, setSess] = useState("");
  const [busy, setBusy] = useState(false);

  const onOpen = (o) => {
    setOpen(o);
    if (o) { setScore(str(lead.score_override)); setPct(str(lead.completion_override)); setSess(str(lead.sessions_override)); }
  };
  const save = async (s, p, n) => {
    setBusy(true);
    try {
      await api.put(`/campaigns/${campaignId}/leads/${lead.id}/override`, {
        score: s === "" ? null : Math.round(Number(s)), completion: p === "" ? null : Number(p),
        sessions: n === "" ? null : Math.round(Number(n)),
      });
      toast.success("Lead updated");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`lead-edit-score-${lead.id}`} title="Edit score / completion / sessions"
          className="ml-1.5 p-1 rounded text-slate-500 hover:text-white hover:bg-white/10 transition-colors"><Pencil className="w-3 h-3" /></button>
      </PopoverTrigger>
      <PopoverContent data-testid="lead-override-popover" className="w-72 bg-[#141A24] border-[#242C3D] text-slate-200 space-y-3">
        <p className="text-xs text-slate-400 truncate">{lead.email}</p>
        <label className="block text-xs text-slate-400 space-y-1">Score (0–100) <span className="text-slate-600">— empty = automatic ({lead.score_override == null ? lead.score : "auto"})</span>
          <input data-testid="lead-override-score" type="number" min={0} max={100} value={score} placeholder="Auto" onChange={(e) => setScore(e.target.value)} className={inputCls} />
        </label>
        <label className="block text-xs text-slate-400 space-y-1">Completion % (0–100) <span className="text-slate-600">— empty = tracked</span>
          <input data-testid="lead-override-completion" type="number" min={0} max={100} step="any" value={pct} placeholder="Tracked" onChange={(e) => setPct(e.target.value)} className={inputCls} />
        </label>
        <label className="block text-xs text-slate-400 space-y-1">Sessions (1+) <span className="text-slate-600">— empty = tracked</span>
          <input data-testid="lead-override-sessions" type="number" min={1} max={1000} step={1} value={sess} placeholder="Tracked" onChange={(e) => setSess(e.target.value)} className={inputCls} />
        </label>
        <div className="flex items-center justify-between pt-1">
          <button type="button" data-testid="lead-override-reset" disabled={busy} onClick={() => save("", "", "")} className="text-xs text-slate-400 hover:text-white underline">Reset to automatic</button>
          <button type="button" data-testid="lead-override-save" disabled={busy || bad(score) || bad(pct) || bad(sess, 1, 1000)} onClick={() => save(score, pct, sess)}
            className="h-8 px-4 rounded-full bg-indigo-500 hover:bg-indigo-400 text-xs font-medium disabled:opacity-50 flex items-center gap-1.5">
            {busy && <Loader2 className="w-3 h-3 animate-spin" />} Save
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
